import { ApolloLink, Observable, type Operation } from "@apollo/client"
import {
  type GraphQLTrace,
  GraphQLTraceKind,
} from "@digits-shared/components/Contexts/GraphQLTracerContext"
import { type GraphQLErrorExtensions } from "graphql/error/GraphQLError"
import { v4 as generateUUID } from "uuid"
import { DG_MUTATION_ERROR } from "@digits-shared/initializers/apollo/middleware/http/dgMutationBlocker"

const SLOW_TRACE_THRESHOLD_MS = 2500

export type TraceCallback = (trace: GraphQLTrace) => void

export type CustomGraphQLErrorExtensions = GraphQLErrorExtensions & {
  code?: string
  originalMessage?: string
}

export default (onTrace?: TraceCallback, shouldLog = () => false) =>
  new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        forward(operation)
          .forEach((response) => {
            if (
              onTrace &&
              response.extensions?.span &&
              response.extensions.span > SLOW_TRACE_THRESHOLD_MS
            ) {
              handleSlowTrace(onTrace, operation, response.extensions.span, shouldLog())
            }

            // Ignore silenced errors as they have already been deemed ignorable. Still including errors
            // for DG mutations as we'll want to display them in GQLTrace UI.
            if (
              onTrace &&
              response.errors?.some(
                (e) => !e.extensions?.silence || e.message === DG_MUTATION_ERROR
              )
            ) {
              // Collection is independent of whether the employee has opened developer tools.
              let kind = GraphQLTraceKind.ERROR

              if (response.errors.some((e) => e.message === DG_MUTATION_ERROR)) {
                kind = GraphQLTraceKind.DG_MUTATION
              }

              const firstError = response?.errors?.[0]
              const firstErrorExtensions = firstError?.extensions

              onTrace({
                id: generateUUID(),
                kind,
                operation,
                durationMS: response.extensions?.span ?? 0,
                recordedAt: Date.now(),
                errorCode: isCustomErrorExtensions(firstErrorExtensions)
                  ? firstErrorExtensions?.code
                  : undefined,
                errorMessage:
                  isCustomErrorExtensions(firstErrorExtensions) &&
                  typeof firstErrorExtensions?.originalMessage === "string"
                    ? firstErrorExtensions.originalMessage
                    : firstError?.message,
                errorPath: firstError?.path?.slice(),
              })
            }

            observer.next(response)
            observer.complete()
          })
          .catch((error) => observer.error(error))
      })
  )

function isCustomErrorExtensions(
  extensions?: GraphQLErrorExtensions
): extensions is CustomGraphQLErrorExtensions {
  return (
    extensions !== undefined &&
    ((extensions as CustomGraphQLErrorExtensions).originalMessage !== undefined ||
      (extensions as CustomGraphQLErrorExtensions).code !== undefined)
  )
}

function handleSlowTrace(
  onSlowTrace: (trace: GraphQLTrace) => void,
  operation: Operation,
  durationMS: number,
  shouldLog: boolean
) {
  const id = generateUUID()
  onSlowTrace({ id, kind: GraphQLTraceKind.SLOW, operation, durationMS, recordedAt: Date.now() })

  // Output the trace of the call stack to provide a crude aid in determine call site.
  /* eslint-disable no-console */
  if (shouldLog) {
    console.groupCollapsed(operation.operationName, `${durationMS}ms`)
    console.log("uuid:", id)
    console.groupCollapsed("variables")
    console.log(operation.variables)
    console.groupEnd()
    console.groupCollapsed("query")
    console.log(operation.query.loc?.source.body)
    console.groupEnd()
    console.groupCollapsed("stack trace")
    console.trace()
    console.groupEnd()
    console.groupEnd()
  }
  /* eslint-enable no-console */
}
